import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { ConfidenceBadgeComponent } from "../plan-shared/confidence-badge/confidence-badge.component";
import { planTaskStatusVisual } from "../plan-shared/plan-task-status";
import type { PlanStatusTone } from "../plan-shared/plan-task-status";
import { PLAN_PHASE_INFO } from "../plan-phase-descriptions";
import { PlanJsonDialogComponent } from "../plan-json-dialog/plan-json-dialog.component";
import { EvidenceProgressStore } from "../../../../services/evidence-progress-store.service";
import { formatConfidence } from "../../../../model/plan-state.utils";
import type { Decision } from "../../../../model/agent-enums.types";
import type { EvaluationContract } from "../../../../model/evaluation-contract.types";
import type { IEvidenceRawEntry } from "../../../../model/evidence-progress.types";
import type { PlanData, PlanPhases, PlanState, PhaseStatus, ValidatorResult } from "../../../../model/plan.types";

/** Dialog data contract — `PlanDebugDialogComponent.open()` injects this via `MAT_DIALOG_DATA`. */
export interface PlanDebugDialogData {
  /** Raw contract Plan aggregate (doc 31 §5) the dialog inspects. */
  plan: PlanData;
  /**
   * Optional live `EvidenceProgressStore` feeding the "Evidence events" section — carried in
   * the data because the store is provided per chat-container, outside the dialog injector.
   * Absent/null renders the section's empty state (backward compatible).
   */
  evidence?: EvidenceProgressStore | null;
}

/** Ids of the 13 debug sections, in display order — index items and content anchors share it. */
export type PlanDebugSectionId =
  | "meta"
  | "phases"
  | "facts"
  | "profile"
  | "estimations"
  | "review"
  | "tasks"
  | "run_config"
  | "pending_decisions"
  | "transition"
  | "diagnosis"
  | "evidence_events"
  | "history";

/** Variants available in the shared `notification-badge` global classes (ai-chat-cards.scss). */
type BadgeVariant = "success" | "error" | "pending" | "running" | "warning";

/** Debug dash for absent values — expected during early phases, never an error state. */
const ABSENT = "—";

/** The 5 data folders of `Plan.phases` (doc 31 §2.4) — fixed display order. */
const PLAN_PHASE_KEYS: ReadonlyArray<keyof PlanPhases> = [
  "semantic_decomposition",
  "capability_resolution",
  "provider_binding",
  "plan_assessment",
  "run_engine",
];

/** The 3 contract-evaluated phases — only these carry decision/retry/confidence columns (doc 31 §5.3). */
const CONTRACT_PHASE_KEYS: ReadonlySet<keyof PlanPhases> = new Set([
  "semantic_decomposition",
  "capability_resolution",
  "provider_binding",
]);

/** Readable titles for the decision types the frontend catalog knows (doc 32 §3.4); unknown types render verbatim. */
const DECISION_TYPE_TITLES: Readonly<Record<string, string>> = {
  plan_approval: "Plan approval",
  dry_run: "Dry run",
};

/** Token-backed color class per semantic tone — mirrors plan-step-list's mapping (component-scoped styles cannot be shared). */
const TONE_CLASSES: Readonly<Record<PlanStatusTone, string>> = {
  ok: "tone-ok",
  err: "tone-err",
  warn: "tone-warn",
  idle: "tone-idle",
  pri: "tone-pri",
};

/** Status icon + tone + word per `PhaseStatus` (plan-cards-spec §Debug dialog Phases table). */
function phaseStatusCopy(status: PhaseStatus): { icon: string; tone: PlanStatusTone; spinning: boolean; word: string } {
  switch (status) {
    case "completed":
      return { icon: "check_circle", tone: "ok", spinning: false, word: "completed" };
    case "in_progress":
      return { icon: "sync", tone: "pri", spinning: true, word: "in progress" };
    case "failed":
      return { icon: "error", tone: "err", spinning: false, word: "failed" };
    case "not_started":
      return { icon: "radio_button_unchecked", tone: "idle", spinning: false, word: "not started" };
  }
}

/** Badge variant per router `Decision` semantics: CONTINUE proceeds, RETRY loops, ASK_USER waits, STOP halts. */
function decisionBadgeVariant(decision: Decision): BadgeVariant {
  switch (decision) {
    case "CONTINUE":
      return "success";
    case "RETRY":
      return "warning";
    case "ASK_USER":
      return "pending";
    case "STOP":
      return "error";
  }
}

/**
 * Icon + tone + word per review `ValidatorResult.status` (doc 31 §5.3): pass → check_circle ok,
 * blocking → error err (the vocabulary has no literal "fail" — blocking IS the failure status),
 * info → info primary.
 */
const REVIEW_STATUS_COPY: Readonly<
  Record<ValidatorResult["status"], { icon: string; toneClass: string; word: string }>
> = {
  pass: { icon: "check_circle", toneClass: "tone-ok", word: "pass" },
  info: { icon: "info", toneClass: "tone-pri", word: "info" },
  blocking: { icon: "error", toneClass: "tone-err", word: "blocking" },
};

/** Badge variant per archived `closed_state` (executed succeeded, failed/rejected did not, the rest stay neutral). */
function historyStateVariant(state: PlanState): BadgeVariant {
  if (state === "executed") return "success";
  if (state === "failed" || state === "rejected") return "error";
  return "pending";
}

/**
 * Minimal local clock format for the evidence table (`HH:MM:SS.mmm`) — locale-free and
 * deterministic (local Date parts, zero-padded). The raw ring stores epoch ms (`at`); the
 * debug table shows the wall-clock processing time for scanability.
 */
const formatEventTime = (at: number): string => {
  const date = new Date(at);
  const pad = (value: number, length = 2): string => String(value).padStart(length, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(
    date.getMilliseconds(),
    3,
  )}`;
};

/** One label/value cell of the shared data grid (label = raw backend key, value = full text). */
interface PlanDebugKvRow {
  label: string;
  value: string;
}

/** One row of the Phases table: status icon, phase identity and the 3 contract-only columns. */
interface PlanDebugPhaseRow {
  key: keyof PlanPhases;
  label: string;
  tooltip: string;
  statusIcon: string;
  toneClass: string;
  spinning: boolean;
  /** Decision badge — the 3 contract phases only, from `contract.verdict_block.decision`. */
  decisionBadge: { text: string; variant: BadgeVariant } | null;
  /** Decision cell fallback: "no contract" for contract phases without a contract, "—" for the other records. */
  decisionText: string;
  /** "↻ n" from `contract.verdict_block.retry_count`, else "—". */
  retriesText: string;
  /** Confidence of the contract's closing verdict; `null` renders the "—" fallback. */
  confidence: number | null;
}

/** One fact block: raw ids as meta and the full value (mono JSON block or inline). */
interface PlanDebugFactView {
  factId: string;
  metaText: string;
  valueIsObject: boolean;
  valueJson: string;
  valueInline: string;
}

/** Full `PlanProfile` readout with "—" fallbacks for every absent piece (profile is null early). */
interface PlanDebugProfileView {
  goals: string[];
  targetText: string;
  batchText: string;
  samplingText: string;
  samplingReason: string;
  taskEntities: { taskId: string; text: string }[];
  workareas: { workareaId: string; usedByText: string; reason: string }[];
}

/** One estimation entry of `estimation_plan` — the 4 raw fields as kv cells. */
interface PlanDebugEstimationView {
  taskId: string;
  estimatesTaskId: string;
  resolversText: string;
  factType: string;
}

/** One review row: validator status icon + full reason + muted refs line. */
interface PlanDebugReviewRowView {
  resultId: string;
  validatorId: string;
  icon: string;
  toneClass: string;
  word: string;
  reason: string;
  refsText: string;
}

/** One task row of the engine run: live state icon + id/attempts + last-attempt readout. */
interface PlanDebugTaskRowView {
  taskId: string;
  icon: string;
  toneClass: string;
  label: string;
  attemptsCount: number;
  resolverId: string;
  bindingsText: string;
  retriesText: string;
}

/** One pending decision row: humanized type, mandatory marker, reason and resolved badge. */
interface PlanDebugDecisionView {
  typeText: string;
  mandatoryText: string;
  reason: string;
  resolvedVariant: "success" | "pending";
  resolvedText: string;
}

/** Diagnosis readout: top-level failure + the run_engine diagnostics rollup. */
interface PlanDebugDiagnosisView {
  failure: { userMessage: string; issueCodesText: string; detail: string } | null;
  lastIssueCodeText: string;
  lastIssueCodeRows: { taskId: string; code: string }[];
  retryChargeText: string;
  retryChargeRows: { taskId: string; charge: number }[];
  pendingRows: { taskId: string; kind: string }[];
  hasAnything: boolean;
}

/** One history row: revision + closed state + note per `PlanRevision` fields. */
interface PlanDebugHistoryRowView {
  revision: number;
  stateText: string | null;
  stateVariant: BadgeVariant;
  objective: string;
  requestedChange: string | null;
  noteMeta: string;
}

/**
 * One raw evidence event row of the debug table (evidence-progress-spec §Debug, v1.1 S8): the
 * 12-key payload cells with "—" for every null — raw numbers verbatim (size/duration stay
 * unformatted here; the debug table is the raw dump, presentation formatting belongs to the
 * evidence card). The label renders truncated with the FULL text as its tooltip.
 */
interface PlanDebugEvidenceRow {
  /** Wall-clock processing time (`HH:MM:SS.mmm`) of the raw ring's `at`. */
  time: string;
  /** Raw stage string — unknown future vocabulary renders verbatim. */
  stage: string;
  /** Raw status string — unknown future vocabulary renders verbatim. */
  status: string;
  /** `tool_id` or "—" when the event carried none. */
  toolId: string;
  /** Task-intent `label` or "—" when the event carried none. */
  label: string;
  /** Full task-intent `label` text for the tooltip; empty when null (no tooltip is shown). */
  labelTooltip: string;
  /** Raw produced-data `kind` or "—" when the event carried none. */
  kind: string;
  /** Raw resolved `layer` or "—" — api_call-only by contract (null on every other stage). */
  layer: string;
  /** Raw `size_bytes` or "—" while null. */
  sizeBytes: string;
  /** Raw `duration_ms` or "—" while null. */
  durationMs: string;
  /** Free-form detail or "—" when absent. */
  detail: string;
  /** 0-based batch row index or "—" for non-batch calls. */
  rowIndex: string;
}

/** One left-index entry: id, icon and label — counts ride the `sectionCounts` record. */
interface PlanDebugSection {
  id: PlanDebugSectionId;
  label: string;
  icon: string;
}

/**
 * Dedicated plan debug dialog (plan-cards-spec §Debug dialog, 1000×680 capped at 90vw/90vh) —
 * replaces the plan card's inline `<details>` debug block: the card opens this dialog via the
 * static `open()` helper and no longer renders its own debug section. Thirteen always-present
 * sections render the raw `PlanData` dump with raw backend field labels; absent values render
 * as "—" (expected during early phases, never an error). The "Evidence events" section appends
 * the live `evidence_progress` raw ring from the optional `EvidenceProgressStore` carried in
 * the dialog data.
 *
 * Live refresh (D3 decision): the dialog renders the STATIC plan snapshot taken at open —
 * neither `PlanBlockStoreService` nor `BlackboardStreamService` exposes a public reactive
 * PlanData source. The store's plan map signal is private (only the non-reactive `byPlanRef`/
 * `byPlanId`/`all()` methods are public) and the blackboard stream deliberately never carries
 * the plan aggregate (doc 31 §3 — the plan never travels by SSE; the stream holds the
 * engine-side `EngineRunState` only). Both services are also provided per chat-container
 * rather than in root, while `MatDialog` instantiates this component from its own injector.
 * When a public PlanData signal/observable appears, merge it into `#plan` with
 * `takeUntilDestroyed` in the constructor; until then the snapshot is correct because the
 * dialog is opened on demand from the card, whose data already mirrors the latest store state.
 */
@Component({
  selector: "app-plan-debug-dialog",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogTitle, MatDialogClose, MatButton, MatIconButton, MatIcon, MatTooltip, ConfidenceBadgeComponent],
  templateUrl: "./plan-debug-dialog.component.html",
  styleUrl: "./plan-debug-dialog.component.scss",
})
export class PlanDebugDialogComponent {
  /**
   * Opens the dialog at the spec'd size — the single entry point the plan card calls. The
   * optional `evidence` store rides the dialog data (it is provided per chat-container, so
   * the dialog's own injector cannot resolve it); null keeps the section's empty state.
   */
  static open(
    dialog: MatDialog,
    plan: PlanData,
    evidence?: EvidenceProgressStore | null,
  ): MatDialogRef<PlanDebugDialogComponent, unknown> {
    return dialog.open(PlanDebugDialogComponent, {
      width: "1000px",
      height: "680px",
      maxWidth: "90vw",
      maxHeight: "90vh",
      data: { plan, evidence: evidence ?? null } satisfies PlanDebugDialogData,
    });
  }

  /** Injected dialog payload — `{ plan, evidence? }`, see `PlanDebugDialogData`. */
  readonly #data = inject<PlanDebugDialogData>(MAT_DIALOG_DATA);

  readonly #host = inject(ElementRef);

  /** `MatDialog` service — reused for the "Plan JSON" action, mirroring the plan card's open call. */
  readonly #dialog = inject(MatDialog);

  /**
   * Optional evidence store for the "Evidence events" section: the null-safe injector probe
   * stays null in practice (the store is per-container, outside the dialog injector), so the
   * data-carried instance passed by the opening card is the effective source; null renders
   * the section's empty state.
   */
  readonly #evidence = inject(EvidenceProgressStore, { optional: true }) ?? this.#data.evidence ?? null;

  /**
   * Plan snapshot rendered by the dialog — static today (see the class doc for the D3
   * decision); already a signal so a future reactive source can merge into it directly.
   */
  readonly #plan = signal<PlanData>(this.#data.plan);

  /** Currently highlighted index section — drives the pill tint and the scroll target. */
  protected readonly activeSection = signal<PlanDebugSectionId>("meta");

  protected readonly plan = this.#plan.asReadonly();

  /** The 13 sections in display order — the left index and the content anchors share this list. */
  protected readonly sections: readonly PlanDebugSection[] = [
    { id: "meta", label: "Meta", icon: "info" },
    { id: "phases", label: "Phases", icon: "account_tree" },
    { id: "facts", label: "Facts", icon: "fact_check" },
    { id: "profile", label: "Profile", icon: "person_search" },
    { id: "estimations", label: "Estimations", icon: "calculate" },
    { id: "review", label: "Review", icon: "rate_review" },
    { id: "tasks", label: "Tasks", icon: "task_alt" },
    { id: "run_config", label: "Run config", icon: "tune" },
    { id: "pending_decisions", label: "Pending decisions", icon: "pending_actions" },
    { id: "transition", label: "Transition", icon: "swap_horiz" },
    { id: "diagnosis", label: "Diagnosis", icon: "troubleshoot" },
    { id: "evidence_events", label: "Evidence events", icon: "timeline" },
    { id: "history", label: "History", icon: "history" },
  ];

  /** Index/header count badges per section — `null` renders no badge (no data present). Phases always counts n / 5. */
  protected readonly sectionCounts = computed<Readonly<Record<PlanDebugSectionId, string | null>>>(() => {
    const plan = this.plan();
    const phases = plan.phases;
    const startedPhases = PLAN_PHASE_KEYS.filter((key) => phases[key].status !== "not_started").length;
    const assessment = phases.plan_assessment;
    const review = assessment.review_outcome;
    const evidenceCount = this.#evidence?.rawEvents().length ?? 0;
    return {
      meta: null,
      phases: `${startedPhases} / ${PLAN_PHASE_KEYS.length}`,
      facts: assessment.facts.length > 0 ? String(assessment.facts.length) : null,
      profile: null,
      estimations: null,
      review: review !== null && review.results.length > 0 ? String(review.results.length) : null,
      tasks: phases.run_engine.task_executions.length > 0 ? String(phases.run_engine.task_executions.length) : null,
      run_config: null,
      pending_decisions:
        review !== null && review.pending_decisions.length > 0 ? String(review.pending_decisions.length) : null,
      transition: null,
      diagnosis: null,
      evidence_events: evidenceCount > 0 ? String(evidenceCount) : null,
      history: plan.history.length > 0 ? String(plan.history.length) : null,
    };
  });

  /** Meta section rows — origin turn, feedback rounds and identity, raw keys verbatim. */
  protected readonly metaRows = computed<PlanDebugKvRow[]>(() => {
    const plan = this.plan();
    return [
      { label: "turn_index", value: plan.turn_index === null ? ABSENT : String(plan.turn_index) },
      { label: "feedback_rounds", value: String(plan.feedback_rounds) },
      { label: "plan_id", value: plan.plan_id || ABSENT },
      { label: "parent_plan_id", value: plan.parent_plan_id ?? ABSENT },
      { label: "state", value: plan.state ?? ABSENT },
      { label: "revision", value: String(plan.revision) },
    ];
  });

  /** Phases table rows — one per phase record, decision/retries/confidence only where a contract exists. */
  protected readonly phaseRows = computed<PlanDebugPhaseRow[]>(() => {
    const phases = this.plan().phases;
    return PLAN_PHASE_KEYS.map((key) => {
      const record = phases[key];
      const info = PLAN_PHASE_INFO[key];
      const status = phaseStatusCopy(record.status);
      const contract: EvaluationContract | null = "contract" in record ? record.contract : null;
      const isContractPhase = CONTRACT_PHASE_KEYS.has(key);
      const decisionBadge =
        contract !== null
          ? { text: contract.verdict_block.decision, variant: decisionBadgeVariant(contract.verdict_block.decision) }
          : null;
      return {
        key,
        label: info.label,
        tooltip: `${info.meaning} — ${status.word}`,
        statusIcon: status.icon,
        toneClass: TONE_CLASSES[status.tone],
        spinning: status.spinning,
        decisionBadge,
        decisionText: isContractPhase ? "no contract" : ABSENT,
        retriesText: contract !== null ? `↻ ${contract.verdict_block.retry_count}` : ABSENT,
        confidence: contract !== null ? contract.verdicts.at(-1)?.confidence ?? null : null,
      };
    });
  });

  /** Fact blocks — raw ids as meta, value as a full-width mono JSON block (objects) or inline. */
  protected readonly factViews = computed<PlanDebugFactView[]>(() =>
    this.plan().phases.plan_assessment.facts.map((fact) => {
      const valueIsObject = typeof fact.value === "object" && fact.value !== null;
      return {
        factId: fact.fact_id,
        metaText: `subject: ${fact.subject} · source: ${fact.source ?? ABSENT} · scope: ${fact.scope}`,
        valueIsObject,
        valueJson: valueIsObject ? JSON.stringify(fact.value, null, 2) : "",
        valueInline: valueIsObject ? "" : fact.value === null || fact.value === undefined ? ABSENT : String(fact.value),
      };
    }),
  );

  /** Profile readout — every field falls back to "—" while `profile` is null (early phases). */
  protected readonly profileView = computed<PlanDebugProfileView>(() => {
    const profile = this.plan().phases.plan_assessment.profile;
    return {
      goals: profile?.goals.goals ?? [],
      targetText: profile ? `${profile.target_entity.entity_type} · ${profile.target_entity.cardinality}` : ABSENT,
      batchText: profile?.batch
        ? `${profile.batch.entity_type} · source: ${
            profile.batch.source.task_id ?? profile.batch.source.evidence_id ?? ABSENT
          }`
        : ABSENT,
      samplingText: profile ? (profile.sampling.eligible ? "eligible" : "not eligible") : ABSENT,
      samplingReason: profile?.sampling.reason || ABSENT,
      taskEntities:
        profile?.task_entities.map((entity) => ({
          taskId: entity.task_id,
          text: `${entity.entity_type} (${entity.mode})`,
        })) ?? [],
      workareas:
        profile?.workareas.map((workarea) => ({
          workareaId: workarea.workarea_id,
          usedByText: workarea.used_by_task_ids.join(", ") || ABSENT,
          reason: workarea.reason || ABSENT,
        })) ?? [],
    };
  });

  /** Estimation entries — the estimating task, its target and the estimated resolvers. */
  protected readonly estimationViews = computed<PlanDebugEstimationView[]>(() =>
    this.plan().phases.plan_assessment.estimation_plan.map((entry) => ({
      taskId: entry.task.task_id,
      estimatesTaskId: entry.estimates_task_id,
      resolversText: entry.estimated_resolver_ids.join(", ") || ABSENT,
      factType: entry.fact_type || ABSENT,
    })),
  );

  /** Review rows — one per validator result, with the muted payload/fact_refs line under the message. */
  protected readonly reviewRows = computed<PlanDebugReviewRowView[]>(() => {
    const results = this.plan().phases.plan_assessment.review_outcome?.results ?? [];
    return results.map((result) => {
      const copy = REVIEW_STATUS_COPY[result.status];
      const refs: string[] = [];
      if (Object.keys(result.payload).length > 0) {
        refs.push(`payload: ${JSON.stringify(result.payload)}`);
      }
      if (result.fact_refs.length > 0) {
        refs.push(`fact_refs: ${result.fact_refs.join(", ")}`);
      }
      return {
        resultId: result.result_id,
        validatorId: result.validator_id,
        icon: copy.icon,
        toneClass: copy.toneClass,
        word: copy.word,
        reason: result.reason || ABSENT,
        refsText: refs.join(" · "),
      };
    });
  });

  /** Task rows — one per `task_executions` entry, resolver/binding from the LAST attempt. */
  protected readonly taskRows = computed<PlanDebugTaskRowView[]>(() =>
    this.plan().phases.run_engine.task_executions.map((execution) => {
      const visual = planTaskStatusVisual(execution.state);
      const lastAttempt = execution.attempts.at(-1) ?? null;
      const bindings =
        lastAttempt !== null && lastAttempt.input_bindings.length > 0
          ? lastAttempt.input_bindings
              .map((binding) => `${binding.role} · ${formatConfidence(binding.confidence)}`)
              .join(", ")
          : ABSENT;
      return {
        taskId: execution.task_id,
        icon: visual.icon,
        toneClass: TONE_CLASSES[visual.tone],
        label: visual.label,
        attemptsCount: execution.attempts.length,
        resolverId: lastAttempt?.resolver_id ?? ABSENT,
        bindingsText: bindings,
        retriesText: `${execution.diagnostics.action_retry_count} · ${execution.diagnostics.hitl_reask_count}`,
      };
    }),
  );

  /** Run config rows — batch consumers and sample size, "—" while the run has no config. */
  protected readonly runConfigRows = computed<PlanDebugKvRow[]>(() => {
    const runConfig = this.plan().phases.run_engine.run_config;
    return [
      {
        label: "batch",
        value: runConfig?.batch ? runConfig.batch.consumer_task_ids.join(", ") || ABSENT : ABSENT,
      },
      { label: "sample", value: runConfig?.sample ? String(runConfig.sample.size) : ABSENT },
    ];
  });

  /** Pending decision rows — catalog title or raw type, Mandatory/Optional marker and resolved badge. */
  protected readonly decisionViews = computed<PlanDebugDecisionView[]>(() => {
    const decisions = this.plan().phases.plan_assessment.review_outcome?.pending_decisions ?? [];
    return decisions.map((decision) => ({
      typeText: DECISION_TYPE_TITLES[decision.decision_type] ?? decision.decision_type,
      mandatoryText: decision.mandatory ? "Mandatory" : "Optional",
      reason: decision.reason || ABSENT,
      resolvedVariant: decision.resolved ? "success" : "pending",
      resolvedText: decision.resolved ? "resolved" : "pending",
    }));
  });

  /** Transition rows — the dump carries state/reason/revision only (no from/to/trigger fields). */
  protected readonly transitionRows = computed<PlanDebugKvRow[]>(() => {
    const plan = this.plan();
    return [
      { label: "state", value: plan.state ?? ABSENT },
      { label: "transition_reason", value: plan.transition_reason || ABSENT },
      { label: "revision", value: String(plan.revision) },
    ];
  });

  /**
   * Diagnosis readout — top-level failure plus the run_engine diagnostics rollup. Uniform
   * diagnostic values collapse to one text (the charge as its sum); mixed values expand to
   * per-task rows instead, per the spec's "pick per-task rows if multiple" rule.
   */
  protected readonly diagnosisView = computed<PlanDebugDiagnosisView>(() => {
    const plan = this.plan();
    const failure = plan.failure;
    const executions = plan.phases.run_engine.task_executions;

    const issueByTask = new Map<string, string>();
    const chargeByTask = new Map<string, number>();
    const pendingByTask = new Map<string, string>();
    for (const execution of executions) {
      if (execution.diagnostics.last_issue_code !== null) {
        issueByTask.set(execution.task_id, execution.diagnostics.last_issue_code);
      }
      if (execution.diagnostics.turn_retry_charge > 0) {
        chargeByTask.set(execution.task_id, execution.diagnostics.turn_retry_charge);
      }
      if (execution.diagnostics.pending_reason !== null) {
        pendingByTask.set(execution.task_id, execution.diagnostics.pending_reason.kind);
      }
    }

    const distinctIssues = [...new Set(issueByTask.values())];
    const distinctCharges = [...new Set(chargeByTask.values())];
    const chargeSum = [...chargeByTask.values()].reduce((sum, charge) => sum + charge, 0);

    const failureView =
      failure !== null
        ? {
            userMessage: failure.user_message || ABSENT,
            issueCodesText: failure.issue_codes.join(", ") || ABSENT,
            detail: failure.detail || ABSENT,
          }
        : null;

    return {
      failure: failureView,
      lastIssueCodeText: distinctIssues.length === 1 ? distinctIssues[0] ?? ABSENT : ABSENT,
      lastIssueCodeRows: distinctIssues.length > 1 ? [...issueByTask].map(([taskId, code]) => ({ taskId, code })) : [],
      retryChargeText: distinctCharges.length === 1 ? String(chargeSum) : ABSENT,
      retryChargeRows:
        distinctCharges.length > 1 ? [...chargeByTask].map(([taskId, charge]) => ({ taskId, charge })) : [],
      pendingRows: [...pendingByTask].map(([taskId, kind]) => ({ taskId, kind })),
      hasAnything:
        failureView !== null || distinctIssues.length > 0 || distinctCharges.length > 0 || pendingByTask.size > 0,
    };
  });

  /** History rows — the archived closed revisions, note line per `PlanRevision` fields. */
  protected readonly historyRows = computed<PlanDebugHistoryRowView[]>(() =>
    this.plan().history.map((revision) => {
      const metaParts: string[] = [];
      if (revision.closed_reason) {
        metaParts.push(revision.closed_reason);
      }
      if (revision.turn_index !== null) {
        metaParts.push(`turn_index: ${revision.turn_index}`);
      }
      if (revision.execution !== null) {
        metaParts.push(`outcome: ${revision.execution.outcome}`);
      }
      return {
        revision: revision.revision,
        stateText: revision.closed_state,
        stateVariant: historyStateVariant(revision.closed_state),
        objective: revision.request_objective || ABSENT,
        requestedChange: revision.requested_change,
        noteMeta: metaParts.join(" · ") || ABSENT,
      };
    }),
  );

  /**
   * Raw evidence ring from the optional store — the store's contract is NEWEST LAST (ring
   * order); empty when no store was passed (the section renders its empty state).
   */
  protected readonly evidenceRawEvents = computed<ReadonlyArray<Readonly<IEvidenceRawEntry>>>(
    () => this.#evidence?.rawEvents() ?? [],
  );

  /**
   * Evidence table rows — reversed copy of the raw ring, NEWEST FIRST for scanability (a debug
   * log reads top-down from the latest event; the ring itself stays untouched). Every null
   * cell renders the "—" dash; unknown stage/status strings pass through verbatim. The v1.1
   * keys use the same rule (`??` also covers legacy events whose keys are absent entirely);
   * the label's tooltip carries the FULL text ("" when null — Material shows no tooltip).
   */
  protected readonly evidenceRows = computed<PlanDebugEvidenceRow[]>(() =>
    [...this.evidenceRawEvents()].reverse().map((entry) => ({
      time: formatEventTime(entry.at),
      stage: entry.event.stage,
      status: entry.event.status,
      toolId: entry.event.tool_id ?? ABSENT,
      label: entry.event.label ?? ABSENT,
      labelTooltip: entry.event.label ?? "",
      kind: entry.event.kind ?? ABSENT,
      layer: entry.event.layer ?? ABSENT,
      sizeBytes: entry.event.size_bytes === null ? ABSENT : String(entry.event.size_bytes),
      durationMs: entry.event.duration_ms === null ? ABSENT : String(entry.event.duration_ms),
      detail: entry.event.detail ?? ABSENT,
      rowIndex: entry.event.row_index === null ? ABSENT : String(entry.event.row_index),
    })),
  );

  /**
   * Highlights the section and smooth-scrolls it into view. The scroll target uses
   * `scrollIntoView` + the sections' `scroll-margin-top` instead of manual `offsetTop`
   * arithmetic: it is correct no matter WHICH ancestor is the effective scroll container
   * (the content column or, before the height chain is bounded, the dialog surface).
   */
  protected scrollToSection(id: PlanDebugSectionId): void {
    this.activeSection.set(id);
    const target = (this.#host.nativeElement as HTMLElement).querySelector<HTMLElement>(`[data-section="${id}"]`);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  /** Opens the raw plan JSON dump (doc 32 §5) — the only entry point left now that the
   *  plan card footer button is gone (response-cards-debug-metadata S6). */
  protected openPlanJson(): void {
    this.#dialog.open(PlanJsonDialogComponent, {
      data: this.plan(),
      width: "700px",
      maxWidth: "95vw",
    });
  }
}
